import { Link } from "react-router-dom";
import classNames from "clsx";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import { AiOutlineEllipsis } from "react-icons/ai";

import { BedStatus } from "@/enums/BedStatus";
import { Avatar } from "@/components/Avatar";
import { Badges } from "./Badges";

type BedCardProps = {
	bed: Bed;
	index: number;
	admissionQueueIsOpen: boolean;
	onAdmitPatient: (bed: Bed) => void;
};

export function BedCard({ bed, index, admissionQueueIsOpen, onAdmitPatient }: BedCardProps) {
	const isBedOccupied = bed.status === BedStatus.OCCUPIED;
	const surgicalAttendance = bed.surgicalAttendance;

	const COL_SIZE = admissionQueueIsOpen ? 6 : 4;
	const COL_SIZE_SM = admissionQueueIsOpen ? 12 : 6;
	const BED_CARD_HEIGHT = "h-18rem";

	const FOOTER_CONTAINER_CLASS_NAME = classNames(
		"d-flex align-items-center w-100",
		isBedOccupied ? "justify-content-between" : "justify-content-end"
	);

	function getBedStatus(bed: Bed) {
		switch (bed.status) {
			case BedStatus.AVAILABLE:
				return 7;
			case BedStatus.UNAVAILABLE:
				return 8;
			default:
				return bed.surgicalAttendance?.status;
		}
	}

	function renderProfessionalAvatars() {
		if (!isBedOccupied || !surgicalAttendance?.team?.members) {
			return null;
		}

		return (
			<div className="d-flex flex-wrap">
				{surgicalAttendance.team.members.slice(0, 4).map((professional, index) => (
					<div className="me-n1" key={`professional-${index}`}>
						<Avatar patientName={professional.name} patientId={professional.id} />
					</div>
				))}
			</div>
		);
	}

	function renderActionButton() {
		if (isBedOccupied) {
			return (
				<div>
					<Link
						className="btn btn-primary hover-elevate-up py-1"
						to={`/atendimento/${bed.surgicalAttendance.attendanceId}/pre-operatorio/admissao-de-enfermagem`}
						title="Ver atendimento"
					>
						Ver
					</Link>
				</div>
			);
		}

		return (
			<Button
				variant={bed.status === BedStatus.AVAILABLE ? "primary" : "secondary"}
				className="hover-elevate-up py-1"
				disabled={bed.status === BedStatus.UNAVAILABLE}
				onClick={() => onAdmitPatient(bed)}
			>
				Admitir paciente
			</Button>
		);
	}

	return (
		<Col lg={COL_SIZE} sm={COL_SIZE_SM} className="mb-6" key={`bed-card-${index}`}>
			<Card>
				<div className={`d-flex flex-column ${BED_CARD_HEIGHT} px-6 py-7`}>
					<div className="d-flex justify-content-between">
						<Card.Title className="fs-5">{bed.fullName}</Card.Title>
						<div className="mt-n4 me-n2">
							<Button variant="secondary" className="px-035 py-0">
								<AiOutlineEllipsis />
							</Button>
						</div>
					</div>

					{isBedOccupied && (
						<Card.Title className="fs-5 m-0">{bed.patient?.name}</Card.Title>
					)}

					<Badges badge={getBedStatus(bed)} />

					{isBedOccupied && (
						<>
							<div className="text-gray-700">
								Procedimento: {surgicalAttendance?.expertise.name}
							</div>
							<div className="text-gray-700">
								Data do Procedimento:{" "}
								{dayjs(surgicalAttendance?.surgeryDate).format("DD/MM/YYYY")}
							</div>
						</>
					)}

					<div className="d-flex flex-grow-1 align-items-end">
						<div className={FOOTER_CONTAINER_CLASS_NAME}>
							{renderProfessionalAvatars()}
							{renderActionButton()}
						</div>
					</div>
				</div>
			</Card>
		</Col>
	);
}
